import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../contexts/SuperAdminAuthContext';
import { superAdminApiService } from '../services/SuperAdminApiService';
import SuperAdminConfigurazioni from './SuperAdminConfigurazioni';
import EmailHistory from '../components/EmailHistory';
import { useConfirm, useToast } from '../../components/ui';
import {
  BarChart3,
  Building2,
  CreditCard,
  Settings,
  Wrench,
  Mail,
  Users,
  Snowflake,
  ClipboardList,
  RefreshCw,
  Save,
  Trash2,
  Play,
  Pause,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  HelpCircle,
  X
} from 'lucide-react';
import './SuperAdminDashboard.css';

interface DashboardStats {
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  totalUsers: number;
  totalProductions: number;
  stripeCustomers: number;
  activeSubscriptions: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
}

interface TenantInfo {
  id: string;
  nome: string;
  status: 'active' | 'suspended';
  users: number;
  productions: number;
  lastActivity: string;
  diskUsage: number;
  stripe?: {
    customerId: string | null;
    subscriptionId: string | null;
    hasStripe: boolean;
  };
}

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { superAdmin, logout } = useSuperAdminAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [stripeStats, setStripeStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingActionKey, setPendingActionKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'tenants' | 'stripe' | 'system' | 'configurations' | 'email-history'>('overview');

  const loadDashboardData = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const [dashboardData, tenantsData] = await Promise.all([
        superAdminApiService.getDashboard(),
        superAdminApiService.getAllTenants()
      ]);
      
      setStats(dashboardData);
      setTenants(tenantsData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei dati');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboardData(true);
  }, [loadDashboardData]);

  const loadStripeData = useCallback(async () => {
    try {
      const stripeData = await superAdminApiService.getStripeStats();
      setStripeStats(stripeData);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento dei dati Stripe');
    }
  }, []);

  const getActionKey = (tenantId: string, action: string) => `${tenantId}:${action}`;
  const isActionPending = (tenantId: string, action: string) => pendingActionKey === getActionKey(tenantId, action);
  const isTenantBusy = (tenantId: string) => pendingActionKey?.startsWith(`${tenantId}:`) ?? false;

  const handleTabChange = (tab: 'overview' | 'tenants' | 'stripe' | 'system' | 'configurations' | 'email-history') => {
    setActiveTab(tab);
    if (tab === 'stripe' && !stripeStats) {
      void loadStripeData();
    }
  };

  const handleTenantAction = async (tenantId: string, action: 'suspend' | 'activate') => {
    const tenant = tenants.find(item => item.id === tenantId);
    const actionLabel = action === 'suspend' ? 'sospendere' : 'riattivare';
    const ok = await confirm({
      title: action === 'suspend' ? 'Sospendi tenant' : 'Riattiva tenant',
      message: `Vuoi ${actionLabel} il tenant "${tenant?.nome || tenantId}"?`,
      variant: action === 'suspend' ? 'danger' : 'default',
      confirmLabel: action === 'suspend' ? 'Sospendi' : 'Riattiva',
    });
    if (!ok) {
      return;
    }

    try {
      setError(null);
      setPendingActionKey(getActionKey(tenantId, action));
      if (action === 'suspend') {
        await superAdminApiService.suspendTenant(tenantId);
      } else {
        await superAdminApiService.activateTenant(tenantId);
      }
      await loadDashboardData();
    } catch (err: any) {
      setError(`Errore ${action}: ${err.message}`);
    } finally {
      setPendingActionKey(null);
    }
  };

  const downloadTenantBackup = async (tenantId: string, tenantName: string) => {
    try {
      setError(null);
      setPendingActionKey(getActionKey(tenantId, 'backup'));
      const blob = await superAdminApiService.getTenantBackup(tenantId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenantName}_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(`Errore download backup: ${err.message}`);
    } finally {
      setPendingActionKey(null);
    }
  };

  const handleGenerateDemoData = async (tenantId: string, tenantName: string) => {
    const ok = await confirm({
      title: 'Genera dati demo',
      message: (
        <>
          <p>Generare dati demo per il tenant "{tenantName}"?</p>
          <p>
            L'operazione crea punti vendita, categorie e allergeni, ingredienti con
            valori nutrizionali, gusti con ricette complete, produzioni degli ultimi
            45 giorni e dati meteo correlati.
          </p>
        </>
      ),
      confirmLabel: 'Genera dati demo',
    });
    if (!ok) return;

    try {
      setError(null);
      setPendingActionKey(getActionKey(tenantId, 'generate-demo'));
      const result = await superAdminApiService.generateDemoData(tenantId);

      if (result.success) {
        const s = result.stats;
        toast.success(
          `Dati demo generati per "${tenantName}": ${s.puntiVendita} punti vendita, ` +
          `${s.gusti} gusti, ${s.ingredienti} ingredienti, ${s.produzioni} produzioni, ` +
          `${s.meteo} record meteo.` +
          (s.errori > 0 ? ` Attenzione: ${s.errori} errori rilevati.` : '')
        );
        await loadDashboardData();
      } else {
        throw new Error(result.error || 'Errore sconosciuto');
      }
    } catch (err: any) {
      setError(`Errore generazione dati demo: ${err.message}`);
    } finally {
      setPendingActionKey(null);
    }
  };

  const handleClearDemoData = async (tenantId: string, tenantName: string) => {
    const ok = await confirm({
      title: 'Cancella dati tenant',
      variant: 'danger',
      message: (
        <>
          <p>
            Questa operazione cancella <strong>tutti i dati</strong> del tenant
            "{tenantName}" mantenendo solo le configurazioni di base.
          </p>
          <p>L'operazione non può essere annullata.</p>
        </>
      ),
      confirmLabel: 'Cancella definitivamente',
      requireText: 'CANCELLA',
    });
    if (!ok) return;

    try {
      setError(null);
      setPendingActionKey(getActionKey(tenantId, 'clear-demo'));
      const result = await superAdminApiService.clearDemoData(tenantId);

      if (result.success) {
        const dettaglio = Object.entries(result.eliminazioni)
          .map(([tabella, count]) => `${tabella}: ${count}`)
          .join(', ');
        toast.success(`Dati cancellati per "${tenantName}". Record eliminati — ${dettaglio}.`);
        await loadDashboardData();
      } else {
        throw new Error(result.error || 'Errore sconosciuto');
      }
    } catch (err: any) {
      setError(`Errore cancellazione dati demo: ${err.message}`);
    } finally {
      setPendingActionKey(null);
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'critical': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy': return <CheckCircle2 size={28} />;
      case 'warning': return <AlertTriangle size={28} />;
      case 'critical': return <AlertOctagon size={28} />;
      default: return <HelpCircle size={28} />;
    }
  };

  if (loading) {
    return (
      <div className="superadmin-loading">
        <div className="loading-spinner"></div>
        <p>Caricamento dashboard...</p>
      </div>
    );
  }

  return (
    <div className="superadmin-dashboard">
      <header className="superadmin-header">
        <div className="header-content">
          <h1>Super Admin Dashboard</h1>
          <div className="user-info">
            <span>Benvenuto, {superAdmin?.username}</span>
            <button onClick={logout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <span>
            <AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            {error}
          </span>
          <button onClick={() => setError(null)} aria-label="Chiudi">
            <X size={16} style={{ verticalAlign: 'text-bottom' }} />
          </button>
        </div>
      )}

      <nav className="dashboard-nav">
        <button 
          className={`nav-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => handleTabChange('overview')}
        >
          <BarChart3 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Panoramica
        </button>
        <button 
          className={`nav-btn ${activeTab === 'tenants' ? 'active' : ''}`}
          onClick={() => handleTabChange('tenants')}
        >
          <Building2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Tenant
        </button>
        <button 
          className={`nav-btn ${activeTab === 'stripe' ? 'active' : ''}`}
          onClick={() => handleTabChange('stripe')}
        >
          <CreditCard size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Stripe
        </button>
        <button 
          className={`nav-btn ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => handleTabChange('system')}
        >
          <Settings size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Sistema
        </button>
        <button 
          className={`nav-btn ${activeTab === 'configurations' ? 'active' : ''}`}
          onClick={() => handleTabChange('configurations')}
        >
          <Wrench size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Configurazioni API
        </button>
        <button 
          className={`nav-btn ${activeTab === 'email-history' ? 'active' : ''}`}
          onClick={() => handleTabChange('email-history')}
        >
          <Mail size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Storico Email
        </button>
      </nav>

      <main className="dashboard-content">
        {activeTab === 'overview' && stats && (
          <div className="overview-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon"><Building2 size={28} /></div>
                <div className="stat-content">
                  <h3>Tenant Totali</h3>
                  <div className="stat-number">{stats.totalTenants}</div>
                  <div className="stat-detail">
                    {stats.activeTenants} attivi, {stats.suspendedTenants} sospesi
                  </div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon"><Users size={28} /></div>
                <div className="stat-content">
                  <h3>Utenti Totali</h3>
                  <div className="stat-number">{stats.totalUsers}</div>
                  <div className="stat-detail">Su tutti i tenant</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon"><Snowflake size={28} /></div>
                <div className="stat-content">
                  <h3>Produzioni</h3>
                  <div className="stat-number">{stats.totalProductions}</div>
                  <div className="stat-detail">Questo mese</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon"><CreditCard size={28} /></div>
                <div className="stat-content">
                  <h3>Clienti Stripe</h3>
                  <div className="stat-number">{stats.stripeCustomers}</div>
                  <div className="stat-detail">di {stats.totalTenants} tenant</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon"><ClipboardList size={28} /></div>
                <div className="stat-content">
                  <h3>Sottoscrizioni</h3>
                  <div className="stat-number">{stats.activeSubscriptions}</div>
                  <div className="stat-detail">Attive</div>
                </div>
              </div>

              <div className="stat-card">
                <div 
                  className="stat-icon"
                  style={{ color: getHealthColor(stats.systemHealth) }}
                >
                  {getHealthIcon(stats.systemHealth)}
                </div>
                <div className="stat-content">
                  <h3>Stato Sistema</h3>
                  <div 
                    className="stat-number"
                    style={{ color: getHealthColor(stats.systemHealth) }}
                  >
                    {stats.systemHealth}
                  </div>
                  <div className="stat-detail">System health</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tenants' && (
          <div className="tenants-tab">
            <div className="tenants-header">
              <h2>Gestione Tenant</h2>
              <button onClick={() => void loadDashboardData()} className="refresh-btn" disabled={refreshing}>
                <RefreshCw size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                {refreshing ? 'Aggiornamento...' : 'Aggiorna'}
              </button>
            </div>
            
            <div className="tenants-grid">
              {tenants.map(tenant => (
                <div key={tenant.id} className={`tenant-card ${tenant.status === 'suspended' ? 'suspended' : ''}`}>
                  <div className="tenant-header">
                    <h3>#{tenant.id} - {tenant.nome}</h3>
                    <span className={`status-badge ${tenant.status}`}>
                      {tenant.status === 'active' ? 'Attivo' : 'Sospeso'}
                    </span>
                  </div>
                  
                  <div className="tenant-stats">
                    <div className="tenant-stat">
                      <span>Utenti:</span>
                      <strong>{tenant.users}</strong>
                    </div>
                    <div className="tenant-stat">
                      <span>Produzioni:</span>
                      <strong>{tenant.productions}</strong>
                    </div>
                    <div className="tenant-stat">
                      <span>Spazio:</span>
                      <strong>{tenant.diskUsage}MB</strong>
                    </div>
                    <div className="tenant-stat">
                      <span>Ultima attività:</span>
                      <strong>{new Date(tenant.lastActivity).toLocaleDateString()}</strong>
                    </div>
                    {tenant.stripe && (
                      <div className="tenant-stat">
                        <span>Stripe:</span>
                        <strong>
                          {tenant.stripe.hasStripe ? (
                            <span style={{ color: 'var(--success)' }}>Configurato</span>
                          ) : (
                            <span style={{ color: '#ef4444' }}>Non configurato</span>
                          )}
                        </strong>
                      </div>
                    )}
                  </div>
                  
                  <div className="tenant-actions">
                    <div className="tenant-primary-actions">
                      <button 
                        onClick={() => navigate(`/superadmin/tenants/${tenant.id}/users`)}
                        className="action-btn users primary"
                        title="Gestisci utenti del tenant"
                        disabled={isTenantBusy(tenant.id)}
                      >
                        <Users size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                        Gestisci utenti
                      </button>

                      <button 
                        onClick={() => downloadTenantBackup(tenant.id, tenant.nome)}
                        className={`action-btn backup ${isActionPending(tenant.id, 'backup') ? 'is-busy' : ''}`}
                        disabled={isTenantBusy(tenant.id)}
                      >
                        <Save size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                        {isActionPending(tenant.id, 'backup') ? 'Backup...' : 'Backup'}
                      </button>
                    </div>

                    <div className="tenant-secondary-actions">
                      <button 
                        onClick={() => handleGenerateDemoData(tenant.id, tenant.nome)}
                        className={`action-btn demo-generate ${isActionPending(tenant.id, 'generate-demo') ? 'is-busy' : ''}`}
                        title="Genera dati fittizi realistici per il tenant"
                        disabled={isTenantBusy(tenant.id)}
                      >
                        {isActionPending(tenant.id, 'generate-demo') ? 'Generazione...' : 'Genera dati demo'}
                      </button>
                      
                      <button 
                        onClick={() => handleClearDemoData(tenant.id, tenant.nome)}
                        className={`action-btn demo-clear ${isActionPending(tenant.id, 'clear-demo') ? 'is-busy' : ''}`}
                        title="Cancella tutti i dati demo del tenant"
                        disabled={isTenantBusy(tenant.id)}
                      >
                        <Trash2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                        {isActionPending(tenant.id, 'clear-demo') ? 'Cancellazione...' : 'Cancella dati'}
                      </button>

                      {tenant.status === 'active' ? (
                        <button 
                          onClick={() => handleTenantAction(tenant.id, 'suspend')}
                          className={`action-btn suspend ${isActionPending(tenant.id, 'suspend') ? 'is-busy' : ''}`}
                          disabled={isTenantBusy(tenant.id)}
                        >
                          <Pause size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                          {isActionPending(tenant.id, 'suspend') ? 'Sospensione...' : 'Sospendi tenant'}
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleTenantAction(tenant.id, 'activate')}
                          className={`action-btn activate ${isActionPending(tenant.id, 'activate') ? 'is-busy' : ''}`}
                          disabled={isTenantBusy(tenant.id)}
                        >
                          <Play size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                          {isActionPending(tenant.id, 'activate') ? 'Riattivazione...' : 'Riattiva tenant'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'stripe' && (
          <div className="stripe-tab">
            <h2>
              <CreditCard size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
              Statistiche Stripe
            </h2>
            
            {stripeStats ? (
              <>
                <div className="stripe-overview">
                  <div className="stripe-stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon"><Building2 size={28} /></div>
                      <div className="stat-content">
                        <h3>Tenant Totali</h3>
                        <div className="stat-number">{stripeStats.overview.totalTenants}</div>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon"><Users size={28} /></div>
                      <div className="stat-content">
                        <h3>Con Customer Stripe</h3>
                        <div className="stat-number">{stripeStats.overview.tenantsWithCustomers}</div>
                        <div className="stat-detail">
                          {Math.round((stripeStats.overview.tenantsWithCustomers / stripeStats.overview.totalTenants) * 100)}% del totale
                        </div>
                      </div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-icon"><ClipboardList size={28} /></div>
                      <div className="stat-content">
                        <h3>Con Subscription</h3>
                        <div className="stat-number">{stripeStats.overview.tenantsWithSubscriptions}</div>
                        <div className="stat-detail">
                          {Math.round((stripeStats.overview.tenantsWithSubscriptions / stripeStats.overview.totalTenants) * 100)}% del totale
                        </div>
                      </div>
                    </div>
                    
                    <div className="stat-card">
                      <div className="stat-icon"><CheckCircle2 size={28} /></div>
                      <div className="stat-content">
                        <h3>Setup Completo</h3>
                        <div className="stat-number">{stripeStats.overview.completeStripeSetup}</div>
                        <div className="stat-detail">Customer + Subscription</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="stripe-details">
                  <h3>Dettagli Tenant Stripe</h3>
                  <div className="stripe-table">
                    <div className="table-header">
                      <div>Nome Tenant</div>
                      <div>Customer ID</div>
                      <div>Subscription ID</div>
                      <div>Status</div>
                      <div>Creato</div>
                    </div>
                    {stripeStats.details.map((tenant: any) => (
                      <div key={tenant.id} className="table-row">
                        <div>{tenant.nome}</div>
                        <div>
                          {tenant.customerId ? (
                            <code>{tenant.customerId}</code>
                          ) : (
                            <span className="missing">—</span>
                          )}
                        </div>
                        <div>
                          {tenant.subscriptionId ? (
                            <code>{tenant.subscriptionId}</code>
                          ) : (
                            <span className="missing">—</span>
                          )}
                        </div>
                        <div>
                          <span className={`stripe-status ${tenant.status}`}>
                            {tenant.status === 'complete' && 'Completo'}
                            {tenant.status === 'customer_only' && 'Solo Customer'}
                            {tenant.status === 'subscription_only' && 'Solo Subscription'}
                            {tenant.status === 'no_stripe' && 'Nessuno'}
                          </span>
                        </div>
                        <div>
                          {new Date(tenant.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="loading-stripe">
                <div className="loading-spinner"></div>
                <p>Caricamento dati Stripe...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'system' && (
          <div className="system-tab">
            <h2>Informazioni Sistema</h2>
            <div className="system-info">
              <div className="info-card">
                <h3>Database</h3>
                <p>PostgreSQL 15</p>
                <p>Stato: Connesso</p>
              </div>
              
              <div className="info-card">
                <h3>Docker</h3>
                <p>Containers attivi: 4</p>
                <p>Stato: Healthy</p>
              </div>
              
              <div className="info-card">
                <h3>Performance</h3>
                <p>CPU: 15%</p>
                <p>RAM: 2.1GB / 8GB</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'configurations' && (
          <SuperAdminConfigurazioni />
        )}

        {activeTab === 'email-history' && (
          <EmailHistory tenants={tenants} />
        )}
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
